import React, { useState, useEffect } from 'react'
import Analytics from 'utils/googleAnalytics'
import { useCookies } from 'react-cookie'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'

import config from 'environment'

import { defaultCategories, STACK, VIEW } from 'types/deck'

import generateInitialState from 'app-redux/cacheables/initialState'
import { ReduxServerProps } from 'app-redux/store'
import { useActions, useActionless, useAppSelector } from 'app-redux/hooks'
import { SET_ACTIVE_STATE, TOGGLE_MODAL } from 'app-redux/active/actions/types'
import { generateInitialDeckState } from 'app-redux/deck/initialState'
import { setDeckStateAction } from 'app-redux/deck/actions'
import { selectCommanderCards, selectStacks, useCanEditDeck } from 'app-redux/deck/selectors'

import WebsocketService from 'services/websocket.service'
import CookieService from 'services/cookie.service'
import LocalStorageService from 'services/localStorage.service'
import { DeckService } from 'services/deck.service'
import { CardService } from 'services/card.service'
import { getControlledStackOptions, getSorting, getStack } from 'services/accountSettings.service'

import useCheckLegality from 'components/deckPage/header/Legality/useCheckLegality'

import PageWrapper from 'components/misc/PageWrapper'
import SandboxMessage from 'components/deckPage/misc/SandboxMessage'
import FilteredViewMessage from 'components/deckPage/misc/FilteredViewMessage'
import EmptyDeckLanding from 'components/deckPage/misc/EmptyDeckLanding'
import TokenContainer from 'components/deckPage/tokens/TokenContainer'
import InlineInhouseAd from 'components/advertisments/InlineInhouseAd'
import InteractionContainer from 'components/deckPage/interactions/InteractionContainer'
import StatsContainer from 'components/deckPage/StatsContainer'
import DeckHeader from 'components/deckPage/header/DeckHeader'
import DeckFilters from 'components/deckPage/filtersAndControls/FilterBar'
import CardDragLayer from 'components/deckPage/dragAndDrop/CardDragLayer'
import NewCategoryDropZoneModal from 'components/deckPage/modals/NewCategoryDropZoneModal'
import DeckSettingsOverlay from 'components/deckPage/settings/DeckSettingsOverlay'
import ImportOverlay from 'components/deckPage/modals/Import'
import ExportModal from 'components/deckPage/modals/Export'
import HistoryModal from 'components/deckPage/deckHistory/historyModal'
import DeckKeybindsOverlay from 'components/deckPage/modals/KeybindsOverlay'
import DeckPageContextMenu from 'components/deckPage/misc/ContextMenu'
import DragDropProvider from 'components/misc/DragDropProvider'
import ImportDropWrapper from 'components/misc/ImportDropWrapper'
import DeckContainerV2 from 'components/deckPage/DeckContainerV2'
import CopyDeck from 'components/deckPage/modals/CopyDeck'
import PackageSelectionOverlay from 'components/deckPage/modals/cardPackages/SelectionOverlay'
import FixedDropZones from 'components/deckPage/misc/FixedDropZones'
import AutofillBasicLandsOverlay from 'components/deckPage/modals/autofillBasicLands'
import CardComparisonOverlay from 'components/deckPage/modals/cardComparisonOverlay'
import NewSnapshotModalStandalone from 'components/deckPage/deckHistory/newSnapshotModalStandalone'
import Icon from 'components/elements/Icon'
import RightPanelV2 from 'components/deckPage/RightPanel'
import FloatingMenu from 'components/misc/FloatingMenu'
import CustomCardListOverlay from 'components/customCards/CustomCardListOverlay'
import CommentBubble from 'components/deckPage/interactions/CommentBubble'
import InteractionOverlay from 'components/deckPage/interactions/InteractionOverlay'
import PinnedCommentContainer from 'components/deckPage/interactions/PinnedCommentContainer'
import StaticCardContainer from 'components/deckPage/viewOptions/StaticCardContainer'
import MobileToolbarV3 from 'components/deckPage/filtersAndControls/MobileToolbarV3'
import ImportFromPrecon from 'components/deckPage/misc/EmptyDeckLanding/buttons/ImportFromPrecon'
import ReportModal from 'components/misc/ReportModal'
import { ShareOverlay } from 'components/deckPage/header/Share'
import { GlobalCateogoryModal } from 'components/deckPage/categories/Modal'
import MultiAddToCollection from 'components/deckPage/modals/MultiAddToCollection'
import XXLDeckLoaderModal from 'components/deckPage/modals/XXLDeckLoader'
import PricingModals from 'components/deckPage/modals/PricingModals'

import { generateDeckDescription, generateDeckUrl } from 'utils/deckSeo'
import { calculateSort, calculateStack } from 'utils/queryParams/deck.queryParams'
import { deckResponseToDeckState, transformToSandboxSafeDeck } from 'utils/responseToDeck'
import { LOG_ERROR_AND_REDIRECT } from 'utils/ErrorString'
import { getDeckFromUrl } from 'utils/deckAsQueryParam'

import styles from './decks.module.scss'
import DeckHelpOverlay from 'components/deckPage/modals/DeckHelpOverlay'

export type Props = { loadedWithForcedPerspectiive?: boolean }

const DeckPage = ({ loadedWithForcedPerspectiive = false }: Props) => {
  const router = useRouter()

  const [setDeckState] = useActions(setDeckStateAction)
  const [setActiveState, toggleModal] = useActionless(SET_ACTIVE_STATE, TOGGLE_MODAL)

  const [{ tbId: userId }] = useCookies(['tbId'])

  const deckId = useAppSelector(state => state.deck.id)
  const deckName = useAppSelector(state => state.deck.name)
  const canEditDeck = useCanEditDeck()
  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)
  const view = useAppSelector(state => state.deck.view)
  const stack = useAppSelector(state => state.deck.stack)
  const controlledStackOptions = useAppSelector(state => state.deck.controlledStackOptions)
  const name = useAppSelector(state => state.deck.name)
  const format = useAppSelector(state => state.deck.format)
  const description = useAppSelector(state => state.deck.description)
  const featured = useAppSelector(state => state.deck.featured)
  const customFeatured = useAppSelector(state => state.deck.customFeatured)
  const deckTags = useAppSelector(state => state.deck.deckTags)
  const commanders = useAppSelector(selectCommanderCards)
  const stacks = useAppSelector(selectStacks)
  const ownerId = useAppSelector(state => state.deck.ownerid)
  const addToCollectionModal = useAppSelector(state => state.active.modals.addToCollection)
  const optimizeCardOverlay = useAppSelector(state => state.active.optimizeCardOverlay)
  const autofillBasicLandsOverlay = useAppSelector(state => state.active.autofillBasicLandsOverlay)
  const selectCustomCards = useAppSelector(state => state.active.selectCustomCards)
  const disableMargins = useAppSelector(state => state.cacheables.disableMargins)
  const staticCard = useAppSelector(state => state.cacheables.staticCard)
  const priceSource = useAppSelector(state => state.active.priceSource)
  const reportDeck = useAppSelector(state => state.active.modals.reportDeck)
  const deckHelpGoals = useAppSelector(state => state.deck.deckHelp?.goals)
  const deckHelpIssues = useAppSelector(state => state.deck.deckHelp?.issues)

  const [loading, setLoading] = useState(true)
  const [forcedPerspective, setForcePerspective] = useState(loadedWithForcedPerspectiive)

  useCheckLegality()

  const sandbox = !deckId
  const filtered = !!router.query?.filter

  const resetSorting = () => {
    const sorting = getSorting()
    const stack = getStack()

    setDeckState({ sorting, stack })
    setForcePerspective(false)
  }

  useEffect(() => {
    const sandboxCachedDeck = LocalStorageService.getObject('sandboxDeck')

    if (sandbox && sandboxCachedDeck) {
      LocalStorageService.remove('sandboxDeck')

      setDeckState(sandboxCachedDeck)
    }

    if (canEditDeck) CookieService.set('tbBanner', customFeatured || featured, 'month')

    if (!sandbox && userId) WebsocketService.initialize(deckId)

    if (config.getIsFirefox()) document.body.style.overflow = 'auto'

    const cleanDeckState = generateInitialDeckState()

    const urlSort = `${router.query.sort || ''}`
    const urlStack = `${router.query.stack || ''}`

    const sorting = urlSort ? calculateSort(urlSort) : null
    const stack = urlStack ? calculateStack(urlStack) : null

    Analytics.signalEvent(`loaded with group by ${cleanDeckState.stack}`)
    Analytics.signalEvent(`loaded with view as ${cleanDeckState.view}`)

    Analytics.signalEvent(`loaded with price source primary as ${priceSource[0]}`)
    Analytics.signalEvent(`loaded with price source secondary as ${priceSource[1]}`)

    Analytics.signalEvent(`deck page loaded logged in ${!!userId}`)
    Analytics.signalEvent(`deck page loaded owned ${`${ownerId}` === `${userId}`}`)

    Analytics.signalEvent(`deck page loaded with static card: ${staticCard ? 'enabled' : 'disabled'}`)

    // TODO - is there a way to move this up in the render cycle?
    const controlledStackOptions = getControlledStackOptions(deckId)

    setDeckState({
      controlledStackOptions,
      stack: stack || cleanDeckState.stack,
      view: cleanDeckState.view,
      sorting: sorting || cleanDeckState.sorting,
    })

    setTimeout(() => setLoading(false), 200)

    return () => {
      setActiveState({ searchOpen: false })

      WebsocketService.uninitialize()
    }
  }, [])

  useEffect(() => {
    const isControlledStacks =
      view === VIEW.CARD && controlledStackOptions?.controlled && (stack === STACK.CUSTOM || stack === STACK.MULTIPLE)

    if (view === VIEW.SPREADSHEET || isControlledStacks) document.body.classList.add('deck-overscroll-view')
    if (isControlledStacks) document.body.classList.add('no-deck-margins')

    return () => {
      document.body.classList.remove('deck-overscroll-view')
      document.body.classList.remove('no-deck-margins')
    }
  }, [view, controlledStackOptions, stack])

  const { title, description: metaDescription } = generateDeckDescription(
    name,
    format,
    description,
    deckTags,
    commanders,
    stacks,
    sandbox,
    deckHelpGoals,
    deckHelpIssues,
  )

  return (
    <ImportDropWrapper>
      <DragDropProvider delayTouchStart={250}>
        <PageWrapper lockable dndPage noSearch title={title} description={metaDescription} noGenericMobileNav>
          <div className={styles.page}>
            <DeckHeader />

            <div className={styles.center}>
              <InlineInhouseAd id="deck-top" className={styles.ad} />
            </div>

            <div className={styles.content}>
              <div className={styles.fullWidth}>
                <div className={styles.tableViewStickyWrapper}>
                  <div className={styles.tableViewStickyContainers}>
                    <div className={styles.innerStickyContainer}>
                      {sandbox && <SandboxMessage />}
                      {filtered && <FilteredViewMessage />}

                      {snapshotMeta && (
                        <div className={styles.originalDeckLink}>
                          <a href={generateDeckUrl(snapshotMeta.parentDeckId, snapshotMeta.parentDeckName)}>
                            <Icon name="arrow left" /> View current deck - {snapshotMeta.parentDeckName}
                          </a>
                        </div>
                      )}

                      {forcedPerspective && !!userId && (
                        <div className={styles.perspectiveContainer}>
                          <div className={styles.forcePerspective}>
                            This deck has been linked with a specific layout and may be overwriting your user settings.
                            This is usually done because the deck creator wants to display the deck in a certain way.
                            You can still&nbsp;
                            <button className={styles.revertButton} onClick={resetSorting}>
                              revert
                            </button>{' '}
                            the settings at any time.
                            <button
                              className={styles.closePerspectiveButton}
                              onClick={() => setForcePerspective(false)}>
                              <Icon name="remove" />
                            </button>
                          </div>
                        </div>
                      )}

                      <DeckFilters />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.primaryContent} style={{ maxWidth: disableMargins ? 'unset' : undefined }}>
                <PinnedCommentContainer />
                <DeckPageContextMenu className={styles.deckPageContextMenu}>
                  <DeckContainerV2 />
                </DeckPageContextMenu>
                <StaticCardContainer />
                <RightPanelV2 />
              </div>

              <div className={styles.tableViewStickyWrapperLower}>
                <div className={styles.tableViewStickyContainers}>
                  <div className={styles.innerStickyContainer}>
                    <InlineInhouseAd id="deck-bottom" scmAd />

                    <StatsContainer />
                    <TokenContainer />

                    {!sandbox && <InteractionContainer />}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!loading && (
            <>
              <CardDragLayer />
              <GlobalCateogoryModal />
              <NewCategoryDropZoneModal />
              <DeckSettingsOverlay />
              <ImportOverlay />
              <CopyDeck />
              <ExportModal />
              <HistoryModal />
              <NewSnapshotModalStandalone />
              <InteractionOverlay />
              <ImportFromPrecon />
              <CommentBubble />
              <ShareOverlay />
              <PricingModals />
              <DeckHelpOverlay />

              {/* <MultiSelectControls /> */}
              <DeckKeybindsOverlay />
              <PackageSelectionOverlay />
              <MultiAddToCollection
                open={addToCollectionModal}
                selectCardsViaCategories={true}
                onClose={() => toggleModal({ modalName: 'addToCollection', flag: false })}
              />
              <XXLDeckLoaderModal />
              <AutofillBasicLandsOverlay
                open={autofillBasicLandsOverlay}
                onClose={() => setActiveState({ autofillBasicLandsOverlay: false })}
              />
              <CardComparisonOverlay />
              <CustomCardListOverlay
                open={selectCustomCards}
                onClose={() => setActiveState({ selectCustomCards: false })}
              />

              <ReportModal
                header="Report deck"
                open={reportDeck}
                onClose={() => toggleModal({ modalName: 'reportDeck', flag: false })}
                url={generateDeckUrl(deckId, deckName)}
                type="Deck"
              />
            </>
          )}

          <EmptyDeckLanding />
          <MobileToolbarV3 />
          <FloatingMenu />
        </PageWrapper>
        <FixedDropZones />
      </DragDropProvider>
    </ImportDropWrapper>
  )
}

export default DeckPage

export const getServerSideProps: GetServerSideProps<Props & ReduxServerProps> = async context => {
  try {
    let deckId: string = ''

    let articleSlug = ''
    let listSlug = ''
    let articleSource = ''

    if (context.params?.id && typeof context.params?.id === 'string') deckId = context.params.id // Works for both loading a deck page & loading a deck page within a sandbox
    if (Array.isArray(context.params?.deckInfo) && context.params?.deckInfo[0]) deckId = context.params.deckInfo[0]

    if (context.params?.article && typeof context.params?.article === 'string') articleSlug = context.params.article
    if (context.params?.list && typeof context.params?.list === 'string') listSlug = context.params.list
    if (context.params?.source && typeof context.params?.source === 'string') articleSource = context.params.source

    const userId = Number(context.req.cookies?.tbId || 0)
    const { show_custom_uploads: showCustomUploads = false } = JSON.parse(context.req.cookies?.settings || '{}')

    const commanderAs = context.query.commanderAs
    const snapshotPage = context.resolvedUrl?.startsWith('/snapshots')
    const sandbox = context.resolvedUrl?.startsWith('/sandbox')

    const cardService = new CardService(context)
    const cleanDeckState = generateInitialDeckState(context)
    const cacheableState = generateInitialState(context.req.cookies)

    const deckAsUrl = context.query.deck ? `${context.query.deck}` : null
    const sorting = typeof context.query.sort === 'string' ? calculateSort(context.query.sort) : null
    const stack = typeof context.query.stack === 'string' ? calculateStack(context.query.stack) : null

    if (sorting !== null) cleanDeckState.sorting = sorting
    if (stack !== null) cleanDeckState.stack = stack

    const props: Props = {
      loadedWithForcedPerspectiive: !!sorting || !!stack,
    }

    if (!deckId && !commanderAs && !deckAsUrl && !(articleSlug && listSlug && articleSource)) return { props } // Base sandbox without other optionss
    if (!deckId && commanderAs) {
      const cards = await cardService.listCardsOnly({ name: commanderAs })
      const card = cards[0]

      if (!card) return { props } // couldn't find it, no big, just drop them in the sandbox

      return {
        props: {
          ...props,
          redux: {
            cacheables: cacheableState,
            deck: {
              ...cleanDeckState,
              cardMap: { [card.id]: { ...card, qty: 1, categories: ['Commander'] } },
              categories: {
                ...defaultCategories,
                Commander: { name: 'Commander', isPremier: true, includedInDeck: true, includedInPrice: true },
              },
            },
          },
        },
      }
    }

    let deck

    if (snapshotPage) deck = await new DeckService(context).getSnapshot(deckId)
    // prettier-ignore
    else if (articleSlug && listSlug && articleSource) deck = await new DeckService(context).getArticleDeck(articleSource, articleSlug, listSlug)
    else if (sandbox && deckAsUrl) deck = await getDeckFromUrl(deckAsUrl, context)
    else {
      // Standard deck lookup
      deck = await new DeckService(context).details(deckId, { limitCardData: true })
      
      if (sandbox) deck = transformToSandboxSafeDeck(deck)
    }

    const mergedDeckStates = deckResponseToDeckState(cleanDeckState, deck, false, userId, showCustomUploads)

    // prettier-ignore
    if (deck.owner.referrerEnum) CookieService.setServerCookie({ 'referred-by': deck.owner.referrerEnum }, context, 60 * 60 * 24)

    // Setting cookie if deck is loaded with custom cards, removes it otherwise
    if (deck.customCards.length) CookieService.setServerCookie({ 'loadedWithCustomCards': 'true' }, context)
    else CookieService.setServerCookie({ 'loadedWithCustomCards': 'false' }, context)

    return { props: { ...props, redux: { deck: mergedDeckStates, cacheables: cacheableState } } }
  } catch (err) {
    return LOG_ERROR_AND_REDIRECT(err, context)
  }
}
